import { css } from "@emotion/css";
import type { PageAction, PrimaryPageAction, SimpleMenuItem } from "@octopusdeploy/design-system-components";
import { Checkbox } from "@octopusdeploy/design-system-components";
import type { ChannelResource, EnvironmentResource, GitRefResource, ResourcesById } from "@octopusdeploy/octopus-server-client";
import { ProcessType, HasRunbooksInGit, Permission } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import { exhaustiveCheck } from "@octopusdeploy/type-utils";
import type { ReactElement, ReactNode } from "react";
import * as React from "react";
import { Action, useProjectScopedAnalyticActionDispatch } from "~/analytics/Analytics";
import { isVersionControlledProcess, processScopedEditPermission } from "~/areas/projects/components/Process/Common/CommonProcessHelpers";
import SideBar from "~/areas/projects/components/Process/Common/SideBar";
import { useProcessSearchFilterContext } from "~/areas/projects/components/Process/Contexts/ProcessSearchFilter/ProcessSearchFilterContext";
import type { ProcessEditorSearchFilterNamedResource } from "~/areas/projects/components/Process/Contexts/ProcessSearchFilter/index";
import type { ProcessListLayoutLoaderLookupData } from "~/areas/projects/components/Process/ProcessListLayoutLoader";
import type { ProcessIdentifier } from "~/areas/projects/components/Process/types";
import { isProcessTemplateProcessIdentifier, isDeploymentProcessIdentifier, isRunbookProcessIdentifier } from "~/areas/projects/components/Process/types";
import { useIsNewProcessUiEnabled } from "~/areas/projects/components/Process/useIsNewProcessUiEnabled";
import { RunbookPageContent } from "~/areas/projects/components/Runbooks/Layouts/RunbookPageContent";
import { RunbookProcessOverviewSidebar } from "~/areas/projects/components/Runbooks/RunbookProcessOverviewSidebar";
import { AddRunbookProcessTemplateStepButton } from "~/areas/projects/components/Steps/AddProcessTemplateStepButton";
import { GitCallouts } from "~/areas/projects/components/VersionControl/GitCallouts";
import { useProjectContext } from "~/areas/projects/context";
import { AdvancedFilters } from "~/components/AdvancedFilterLayout/AdvancedFilters";
import type { DoBusyTask } from "~/components/DataBaseComponent";
import FilterSearchBox from "~/components/FilterSearchBox/index";
import { PageContent } from "~/components/PageContent/PageContent";
import { isAllowed, isAllowedToRunGitRunbook } from "~/components/PermissionCheck/PermissionCheck";
import { PermissionCheck } from "~/components/PermissionCheck/index";
import EnvironmentSelect from "~/components/form/EnvironmentSelect/EnvironmentSelect";
import { Select } from "~/components/form/index";
import { useOctopusFeatureToggle } from "~/hooks/useOctopusFeatureToggle";
import { LastPublishedChip } from "../../Runbooks/LastPublishedChip";
import PublishButton, { usePublishedRunbookSnapshotSetup } from "../../Runbooks/PublishButton";
import { getPermissionDeniedTooltipText, RunNowButton } from "../../Runbooks/RunNowButton";
import { useRunbookContext } from "../../Runbooks/RunbookContext";
import ContextAddStepNavigationButton from "../../Steps/ContextAddStepNavigationButton";
import { GitRepositoryErrorCallout } from "../../Variables/GitRepositoryErrorCallout";
import { useProcessContext } from "../Contexts/ProcessContext";
export interface ProcessContextPaperLayoutProps {
    children?: ReactNode;
    titleAccessory?: ReactElement;
    overflowActions?: SimpleMenuItem[];
    processIdentifier: ProcessIdentifier;
    doBusyTask: DoBusyTask;
    gitRefResource: GitRefResource | undefined;
    processListLookupData: ProcessListLayoutLoaderLookupData;
    showStepNotes: boolean;
    onChangeShowStepNotes: (val: boolean) => void;
}
export const ProcessContextPaperLayout: React.FC<ProcessContextPaperLayoutProps> = ({ children, doBusyTask, gitRefResource, processIdentifier, overflowActions, titleAccessory, processListLookupData, showStepNotes, onChangeShowStepNotes }) => {
    if (isRunbookProcessIdentifier(processIdentifier)) {
        return (<RunbooksProcessContextPaperLayout doBusyTask={doBusyTask} gitRefResource={gitRefResource} processIdentifier={processIdentifier} overflowActions={overflowActions} titleAccessory={titleAccessory} processListLookupData={processListLookupData} showStepNotes={showStepNotes} onChangeShowStepNotes={onChangeShowStepNotes}>
                <GitCallouts isRunbooksPage gitRefResource={gitRefResource}/>
                <div className={processOverviewContainerStyles}>{children}</div>
            </RunbooksProcessContextPaperLayout>);
    }
    if (isDeploymentProcessIdentifier(processIdentifier)) {
        return (<DeploymentProcessContextPaperLayout gitRefResource={gitRefResource} processIdentifier={processIdentifier} overflowActions={overflowActions} titleAccessory={titleAccessory} processListLookupData={processListLookupData} showStepNotes={showStepNotes} onChangeShowStepNotes={onChangeShowStepNotes}>
                <GitCallouts gitRefResource={gitRefResource}/>
                <div className={processOverviewContainerStyles}>{children}</div>
            </DeploymentProcessContextPaperLayout>);
    }
    if (isProcessTemplateProcessIdentifier(processIdentifier)) {
        return <>{children}</>;
    }
    exhaustiveCheck(processIdentifier, "Unknown process type");
};
ProcessContextPaperLayout.displayName = "ProcessContextPaperLayout"
const processOverviewContainerStyles = css({
    // Stretches the dotted background to the remaining container space
    flex: 1,
});
const DeploymentProcessContextPaperLayout: React.FC<Omit<ProcessContextPaperLayoutProps, "doBusyTask">> = ({ processIdentifier, titleAccessory, gitRefResource, children, overflowActions, processListLookupData, showStepNotes, onChangeShowStepNotes, }) => {
    const processContext = useProcessContext();
    const projectContext = useProjectContext();
    const project = projectContext.state.model;
    const hasValidProcess: boolean = processContext.selectors.hasValidProcess();
    const hasSteps = processContext.selectors.hasSteps();
    const dispatchAction = useProjectScopedAnalyticActionDispatch(projectContext.state.model.Id);
    const isNewProcessUiEnabled = useIsNewProcessUiEnabled();
    const processSearchFilterContext = useProcessSearchFilterContext();
    const { state: processSearchFilterContextLookupsState, actions: processSearchFilterContextActions } = processSearchFilterContext;
    const pageActions: PageAction[] = [];
    React.useEffect(() => {
        projectContext.actions.refreshGitVariableErrors();
        // We only want to check that the variables when this component is created. No need for any dependencies to trigger a re-check.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    if (hasSteps || project?.IsVersionControlled) {
        const createReleaseButton: PageAction = {
            type: "navigate",
            buttonType: "secondary",
            label: "Create Release",
            path: links.createReleasePage.generateUrl({ spaceId: project.SpaceId, projectSlug: project.Slug }),
            onClick: () => dispatchAction("Create a release", { resource: "Create Release", action: Action.Add }),
            hasPermissions: isAllowed({ permission: Permission.ReleaseCreate, project: project.Id, tenant: "*" }),
        };
        pageActions.push(createReleaseButton);
    }
    if (processContext.selectors.hasSteps()) {
        pageActions.push(getContextAddStepNavigationPageAction(processIdentifier.type, project.Id, gitRefResource));
    }
    const sidebar = isNewProcessUiEnabled && hasValidProcess && hasSteps ? (<SideBar processContextState={processContext.state} includedScriptModules={processListLookupData.includedScriptModules} lifecyclePreview={processListLookupData.lifecyclePreview} environments={Object.values(processListLookupData.environmentsById || {})} onDataChanged={() => processContext.actions.refreshLookupData()}/>) : undefined;
    const filters = isNewProcessUiEnabled && hasValidProcess && hasSteps
        ? {
            inputs: [
                <FilterSearchBox placeholder="Filter by name..." value={processSearchFilterContextLookupsState.searchFilter.filterKeyword} onChange={(filterKeyword) => processSearchFilterContextActions.onFilterChange((prev) => ({ ...prev, filterKeyword }))} autoFocus={true}/>,
                <Checkbox label={"Show notes"} value={showStepNotes} onChange={onChangeShowStepNotes} noMargin={true}/>,
            ],
            advancedFilters: {
                content: <ProcessListAdvancedFilters processType={processContext.selectors.getProcessType()} environmentLookupData={processListLookupData.environmentsById} channelLookupData={processListLookupData.channelsById ?? undefined}/>,
                onResetFilter: () => processSearchFilterContextActions.onFilterChange(() => processSearchFilterContext.getEmptyFilter()),
                hasUserSelectedValues: processSearchFilterContext.isFiltering,
            },
        }
        : undefined;
    return (<PageContent header={{ title: "Process", pageActions, overflowActions, contextSelector: titleAccessory }} sidebar={sidebar} filters={filters}>
            {children}
        </PageContent>);
};
DeploymentProcessContextPaperLayout.displayName = "DeploymentProcessContextPaperLayout"
const RunbooksProcessContextPaperLayout: React.FC<ProcessContextPaperLayoutProps> = ({ doBusyTask, processIdentifier, overflowActions, titleAccessory, gitRefResource, children, processListLookupData, showStepNotes, onChangeShowStepNotes }) => {
    const projectContext = useProjectContext();
    const project = projectContext.state.model;
    const runbooksContext = useRunbookContext();
    const runbook = runbooksContext.state.runbook;
    const gitRef = projectContext.state.gitRef ? projectContext.state.gitRef.CanonicalName : undefined;
    const allowedToRunOnCurrentBranch = isAllowedToRunGitRunbook(project, gitRef);
    const hasGitRunbooks = HasRunbooksInGit(project.PersistenceSettings);
    const isProcessUiVNextEnabled = useIsNewProcessUiEnabled();
    const processContext = useProcessContext();
    const hasValidProcess: boolean = processContext.selectors.hasValidProcess();
    const hasSteps = processContext.selectors.hasSteps();
    const processSearchFilterContext = useProcessSearchFilterContext();
    const { state: processSearchFilterContextLookupsState, actions: processSearchFilterContextActions } = processSearchFilterContext;
    React.useEffect(() => {
        projectContext.actions.refreshGitVariableErrors();
        // We only want to check that the variables when this component is created. No need for any dependencies to trigger a re-check.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    if (!runbook) {
        throw Error("runbook value should exist");
    }
    const publishedRunbookAndTemplate = usePublishedRunbookSnapshotSetup(doBusyTask, runbook);
    const publishedRunbookSnapshot = publishedRunbookAndTemplate?.publishedRunbookSnapshot;
    const runbookRunTemplate = publishedRunbookAndTemplate?.runbookRunTemplate;
    const isRunbookRunTemplateModified = runbookRunTemplate && (runbookRunTemplate.IsRunbookProcessModified || runbookRunTemplate.IsVariableSetModified || runbookRunTemplate.IsLibraryVariableSetModified);
    const titleChip = publishedRunbookSnapshot && <LastPublishedChip project={project} publishedRunbookSnapshot={publishedRunbookSnapshot} isRunbookRunTemplateModified={isRunbookRunTemplateModified}/>;
    const isBlueprintsEnabled = useOctopusFeatureToggle("blueprints", false);
    const runNowPageAction: PrimaryPageAction = {
        type: "custom",
        key: "Run Now",
        content: (<RunNowButton spaceId={runbook.SpaceId} isDisabled={!hasSteps || (hasGitRunbooks && !allowedToRunOnCurrentBranch)} tooltip={hasGitRunbooks && !allowedToRunOnCurrentBranch ? getPermissionDeniedTooltipText(gitRef) : undefined} projectSlug={project.Slug} projectId={project.Id} runbookId={runbook.Id}/>),
        hasPermissions: isAllowed({ permission: Permission.RunbookRunCreate, project: project.Id, wildcard: true }),
    };
    const pageActions: PageAction[] = [];
    if (!isVersionControlledProcess(project.PersistenceSettings, processContext.state.processIdentifier.type)) {
        const publishAction: PageAction = {
            type: "custom",
            key: "Publish",
            content: <PublishButton publishedRunbookSnapshot={publishedRunbookSnapshot} isRunbookRunTemplateModified={isRunbookRunTemplateModified}/>,
            hasPermissions: isAllowed({ permission: Permission.RunbookEdit, project: project.Id, wildcard: true }),
        };
        pageActions.push(publishAction);
    }
    const addStepAction = getContextAddStepNavigationPageAction(processIdentifier.type, project.Id, gitRefResource);
    pageActions.push(addStepAction);
    if (isBlueprintsEnabled) {
        const addProcessTemplateStepButton: PageAction = {
            type: "custom",
            key: "Add Process Template",
            content: <AddRunbookProcessTemplateStepButton projectId={project.Id}/>,
            hasPermissions: isAllowed({ permission: Permission.RunbookEdit, project: project.Id, wildcard: true }),
        };
        pageActions.push(addProcessTemplateStepButton);
    }
    const sidebar = isProcessUiVNextEnabled && hasValidProcess && hasSteps ? (<RunbookProcessOverviewSidebar spaceId={project.SpaceId} projectId={project.Id} onDataChanged={() => processContext.actions.refreshLookupData()} includedScriptModules={processListLookupData.includedScriptModules}/>) : undefined;
    const filters = isProcessUiVNextEnabled && hasValidProcess && hasSteps
        ? {
            inputs: [
                <FilterSearchBox placeholder="Filter by name..." value={processSearchFilterContextLookupsState.searchFilter.filterKeyword} onChange={(filterKeyword) => processSearchFilterContextActions.onFilterChange((prev) => ({ ...prev, filterKeyword }))} autoFocus={true}/>,
                <Checkbox label={"Show notes"} value={showStepNotes} onChange={onChangeShowStepNotes} noMargin={true}/>,
            ],
            advancedFilters: {
                content: <ProcessListAdvancedFilters processType={processContext.selectors.getProcessType()} environmentLookupData={processListLookupData.environmentsById}/>,
                onResetFilter: () => processSearchFilterContextActions.onFilterChange(() => processSearchFilterContext.getEmptyFilter()),
                hasUserSelectedValues: processSearchFilterContext.isFiltering,
            },
        }
        : undefined;
    return (<RunbookPageContent header={{ title: runbook.Name, primaryAction: runNowPageAction, pageActions, chip: titleChip, overflowActions, contextSelector: titleAccessory }} sidebar={sidebar} filters={filters} project={project}>
            <GitRepositoryErrorCallout calloutType={"danger"} additionalIntroText="The runbook can not be published until the error is resolved."></GitRepositoryErrorCallout>
            {children}
        </RunbookPageContent>);
};
RunbooksProcessContextPaperLayout.displayName = "RunbooksProcessContextPaperLayout"
interface ProcessListAdvancedFiltersProps {
    processType: ProcessType;
    environmentLookupData: ResourcesById<EnvironmentResource>;
    channelLookupData?: ResourcesById<ChannelResource>;
}
export function ProcessListAdvancedFilters({ processType, environmentLookupData, channelLookupData }: ProcessListAdvancedFiltersProps) {
    const processSearchFilterContext = useProcessSearchFilterContext();
    const { state: processSearchFilterContextLookupsState, actions: processSearchFilterContextActions } = processSearchFilterContext;
    return (<AdvancedFilters>
            <PermissionCheck permission={Permission.EnvironmentView} wildcard={true}>
                <EnvironmentSelect value={processSearchFilterContextLookupsState.searchFilter.environment?.Id} onChange={(environmentId) => {
            // eslint-disable-next-line @typescript-eslint/init-declarations
            let environment: ProcessEditorSearchFilterNamedResource;
            if (environmentId) {
                const resource = environmentLookupData[environmentId];
                environment = {
                    Id: resource.Id,
                    Name: resource.Name,
                };
            }
            processSearchFilterContextActions.onFilterChange((t) => ({ ...t, environment }));
        }} environments={Object.values(environmentLookupData)} allowClear={true} allowFilter={true} fieldName="environment"/>
            </PermissionCheck>
            {processType === ProcessType.Deployment && channelLookupData && Object.keys(channelLookupData).length > 1 && (<Select value={processSearchFilterContextLookupsState.searchFilter.channel?.Id} onChange={(channelId) => {
                // eslint-disable-next-line @typescript-eslint/init-declarations
                let channel: ProcessEditorSearchFilterNamedResource;
                if (channelId) {
                    const resource = channelLookupData[channelId];
                    channel = {
                        Id: resource.Id,
                        Name: resource.Name,
                    };
                }
                processSearchFilterContextActions.onFilterChange((t) => ({ ...t, channel }));
            }} items={Object.values(channelLookupData).map((e) => ({ value: e.Id, text: e.Name }))} allowClear={true} allowFilter={true} fieldName="channel"/>)}
            <Checkbox label="Include unscoped steps" value={!!processSearchFilterContextLookupsState.searchFilter.includeUnscoped} onChange={(includeUnscoped) => {
            processSearchFilterContextActions.onFilterChange((prev) => ({ ...prev, includeUnscoped }));
        }}/>
        </AdvancedFilters>);
}
function getContextAddStepNavigationPageAction(processType: ProcessType, projectId: string, gitRefResource: GitRefResource | undefined): PageAction {
    return {
        type: "custom",
        key: "Context Add Step Navigation",
        content: <ContextAddStepNavigationButton gitRefResource={gitRefResource}/>,
        hasPermissions: isAllowed({
            permission: processScopedEditPermission(processType),
            project: projectId,
            wildcard: true,
        }),
    };
}
